/**
 * FirstClientStep - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * OPTIONNEL - Premier client
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, ArrowRight, SkipForward, Users } from 'lucide-react';
import { clientsAPI } from '@/utils/clientsAPI';
import { logger } from '@/utils/logger';

interface FirstClientStepProps {
  onComplete: () => void;
  onSkip: () => void;
}

// Glass Input component
const GlassInput: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  error?: string;
  className?: string;
}> = ({ label, value, onChange, onBlur, placeholder, required, type = 'text', error, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-white/80 mb-2">
      {label} {required && <span className="text-lime-400">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      className={`w-full px-4 py-3 bg-white/10 backdrop-blur-md border rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300 ${
        error ? 'border-red-400/50' : 'border-white/20'
      }`}
      placeholder={placeholder}
    />
    {error && <p className="mt-1 text-sm text-red-400">{error}</p>}
  </div>
);

// Glass Button component
const GlassButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  isLoading?: boolean;
  className?: string;
  type?: 'button' | 'submit';
}> = ({ children, onClick, disabled, variant = 'primary', isLoading, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300
      ${variant === 'primary'
        ? 'bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 hover:shadow-lg hover:shadow-lime-500/30 hover:-translate-y-0.5 disabled:opacity-50'
        : variant === 'ghost'
        ? 'text-white/60 hover:text-white hover:bg-white/10'
        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
    ) : children}
  </button>
);

export const FirstClientStep: React.FC<FirstClientStepProps> = ({ onComplete, onSkip }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    addressLine1: '',
    zipCode: '',
    city: '',
    type: 'COMPANY' as const, // Type explicite pour que companyName soit utilisé
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const getFieldError = (field: string): string | undefined => {
    if (!touched[field]) return undefined;

    if (field === 'companyName' && !formData.companyName.trim()) {
      return t('onboarding.client.required');
    }
    if (field === 'email' && formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return t('onboarding.client.invalidEmail');
    }

    return undefined;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);

    if (!formData.companyName.trim()) {
      setError(t('onboarding.client.nameRequired'));
      return;
    }

    try {
      setIsLoading(true);
      await clientsAPI.create(formData);
      logger.log('✅ Client créé');
      onComplete();
    } catch (err: any) {
      logger.error('❌ Erreur création client:', err);
      setError(err.message || t('onboarding.client.createError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('onboarding.client.title')}
        </h1>
        <p className="text-white/60">
          {t('onboarding.client.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main card (2/3) */}
        <div className="lg:col-span-2">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center">
                <UserPlus size={20} className="text-lime-400" />
              </div>
              <h2 className="text-lg font-semibold text-white">
                {t('onboarding.client.clientInfo')}
              </h2>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl text-white">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <GlassInput
                label={t('onboarding.client.clientName')}
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                onBlur={() => handleBlur('companyName')}
                error={getFieldError('companyName')}
                placeholder={t('onboarding.client.clientNamePlaceholder')}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GlassInput
                  label={t('onboarding.client.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  error={getFieldError('email')}
                  placeholder="client@example.com"
                />

                <GlassInput
                  label={t('onboarding.client.phone')}
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="01 23 45 67 89"
                />
              </div>

              <GlassInput
                label={t('onboarding.client.address')}
                value={formData.addressLine1}
                onChange={(e) => handleChange('addressLine1', e.target.value)}
                placeholder="123 Rue Example"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GlassInput
                  label={t('onboarding.client.postalCode')}
                  value={formData.zipCode}
                  onChange={(e) => handleChange('zipCode', e.target.value)}
                  placeholder="75001"
                />

                <GlassInput
                  label={t('onboarding.client.city')}
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder={t('onboarding.client.cityPlaceholder')}
                />
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar (1/3) */}
        <div className="space-y-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Users size={16} className="text-lime-400" />
              </div>
              <h3 className="text-base font-semibold text-white">
                {t('onboarding.client.management')}
              </h3>
            </div>
            <p className="text-sm text-white/60">
              {t('onboarding.client.managementInfo')}
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
            <p className="text-sm text-white/50">
              <span className="text-lime-400">{t('onboarding.client.tipLabel')}</span> {t('onboarding.client.tip')}
            </p>
          </div>

          <div className="space-y-3 pt-4">
            <GlassButton
              onClick={onSkip}
              disabled={isLoading}
              variant="ghost"
              className="w-full"
            >
              <SkipForward className="w-5 h-5" />
              {t('onboarding.client.skip')}
            </GlassButton>

            <GlassButton
              onClick={() => handleSubmit()}
              disabled={!formData.companyName.trim() || isLoading}
              isLoading={isLoading}
              className="w-full"
            >
              {t('onboarding.client.continue')}
              {!isLoading && <ArrowRight className="w-5 h-5" />}
            </GlassButton>
          </div>
        </div>
      </div>
    </div>
  );
};
